// Admin query layer. Pairs with `lib/views/admin.ts` + `lib/views/admin-resources.ts`
// (server fetchers used during SSR prefetch) and `lib/mutations/admin.ts`.
// No 'use client' directive: hooks are React hooks called from client
// components; server pages import the query keys to prefetch.

import {
  infiniteQueryOptions,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  keepPreviousData,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ResourceKind } from '@leanin/shared/resource-kinds';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';

// ---- Re-exported parameter types (mirror views helpers) ----

export type AnalyticsWindowDays = 30 | 90 | 180 | 365;
export const DEFAULT_ANALYTICS_WINDOW_DAYS: AnalyticsWindowDays = 90;

export type MemberAdminStatus =
  | 'active'
  | 'pending'
  | 'restricted'
  | 'deactivated'
  | 'banned';

export type AdminPlatformRoleFilter = 'admin' | 'moderator' | 'none';
export type AdminCommunityRoleFilter = 'circle_leader' | 'network_leader';
export interface ListAdminMembersInput {
  readonly q?: string;
  readonly status?: MemberAdminStatus;
  readonly platformRole?: AdminPlatformRoleFilter;
  readonly communityRole?: AdminCommunityRoleFilter;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface InvitationsPageParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface GroupInvitesPageParams {
  readonly status?: 'pending' | 'accepted' | 'revoked' | 'expired';
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface AuditPageParams {
  readonly action?: string;
  readonly actor?: string;
  readonly outcome?: 'success' | 'failure';
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface ConsentPageParams {
  readonly userId?: string;
  readonly purpose?:
    | 'marketing'
    | 'analytics'
    | 'email_digest'
    | 'ai_processing'
    | 'directory_visible'
    | 'ranking_signal'
    | 'cookie_strict'
    | 'cookie_analytics'
    | 'cookie_marketing';
  readonly granted?: boolean;
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface DsrPageParams {
  readonly status?:
    | 'received'
    | 'approved'
    | 'running'
    | 'completed'
    | 'rejected'
    | 'overdue'
    | 'cancelled';
  readonly kind?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface SensitiveReadPageParams {
  readonly actor?: string;
  readonly resourceKind?:
    | 'member_profile'
    | 'message_thread'
    | 'dsr_export_bundle'
    | 'audit_log_query'
    | 'consent_history'
    | 'bulk_export';
  readonly resourceId?: string;
  readonly from?: string;
  readonly to?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export interface BroadcastsPageParams {
  readonly status?: 'draft' | 'scheduled' | 'sent';
  readonly cursor?: string;
  readonly pageSize?: number;
  readonly q?: string;
}

export interface AdminReportQueueInput {
  readonly cursor?: string;
  readonly pageSize?: number;
  readonly status?: 'open' | 'triaged' | 'actioned' | 'dismissed';
  readonly targetKind?:
    | 'post'
    | 'comment'
    | 'message'
    | 'event'
    | 'resource'
    | 'member';
  readonly reason?:
    | 'spam'
    | 'harassment'
    | 'self_harm'
    | 'illegal'
    | 'other';
}

export type AdminResourceStatus = 'all' | 'draft' | 'published' | 'featured';
export type AdminResourceContentLayer =
  | 'community'
  | 'network'
  | 'public';

export interface ListAdminResourcesInput {
  readonly status?: AdminResourceStatus;
  readonly contentLayer?: AdminResourceContentLayer;
  readonly kind?: ResourceKind;
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export type EngagementScope = 'circles' | 'networks';
export type EngagementPeriod = '30d' | '90d' | 'lifetime';
export type EngagementSortBy = 'activityScore' | 'memberCount' | 'lastActivity';
export type EngagementSortDir = 'asc' | 'desc';

export interface EngagementListParams {
  readonly scope: EngagementScope;
  readonly period: EngagementPeriod;
  readonly sortBy: EngagementSortBy;
  readonly sortDir: EngagementSortDir;
  readonly pageSize?: number;
}

export interface EngagementDetailParams {
  readonly scope: 'circle' | 'network';
  readonly id: string;
  readonly period: EngagementPeriod;
}

export type AudienceSpec =
  | { readonly kind: 'all' }
  | { readonly kind: 'circles'; readonly circleIds: readonly string[] }
  | { readonly kind: 'networks'; readonly networkIds: readonly string[] }
  | { readonly kind: 'members'; readonly userIds: readonly string[] }
  | { readonly kind: 'role'; readonly role: 'leader' | 'admin' }
  | { readonly kind: 'recent_active'; readonly days: number };

export interface PickerInput {
  readonly q?: string;
  readonly cursor?: string;
  readonly pageSize?: number;
}

// ---- Query keys ----

export const adminKeys = {
  all: ['admin'] as const,

  analytics: (windowDays: AnalyticsWindowDays = DEFAULT_ANALYTICS_WINDOW_DAYS) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'analytics', windowDays] as const,
      queryFn: () => fetchAdminAnalytics(windowDays),
    }),

  // Inner-node prefix: every member-scoped cache (list/detail/counts/search).
  members: () => [...adminKeys.all, 'members'] as const,

  // membersList — flat (`useAdminMembersPage`) + infinite
  // (`useInfiniteAdminMembers`) share this leaf's key; cursor via pageParam.
  membersList: (params: ListAdminMembersInput) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.members(), 'list', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminMembersPage({
          ...params,
          cursor: (pageParam as string | undefined) ?? params.cursor,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) =>
        (lastPage as { nextCursor?: string | null }).nextCursor ?? undefined,
    }),

  memberDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'detail', id] as const,
      queryFn: () => fetchAdminMemberDetail(id),
      enabled: id.length > 0,
    }),

  memberStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'status-counts'] as const,
      queryFn: fetchAdminMemberStatusCounts,
    }),

  memberSearch: (params: PickerInput) =>
    queryOptions({
      queryKey: [...adminKeys.members(), 'search', params] as const,
      queryFn: () => searchAdminMembersForPicker(params),
    }),

  invitations: (params: InvitationsPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'invitations', params] as const,
      queryFn: () => fetchAdminInvitations(params),
    }),

  groupInvites: (params: GroupInvitesPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'group-invites', params] as const,
      queryFn: () => fetchAdminGroupInvites(params),
    }),

  audit: (params: AuditPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'audit', params] as const,
      queryFn: () => fetchAdminAudit(params),
    }),

  consent: (params: ConsentPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'consent', params] as const,
      queryFn: () => fetchAdminConsent(params),
    }),

  dsr: (params: DsrPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'dsr', 'list', params] as const,
      queryFn: () => fetchAdminDsr(params),
    }),

  dsrDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'dsr', 'detail', id] as const,
      queryFn: () => fetchAdminDsrDetail(id),
      enabled: id.length > 0,
    }),

  sensitiveRead: (params: SensitiveReadPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'sensitive-read', params] as const,
      queryFn: () => fetchAdminSensitiveRead(params),
    }),

  // Inner-node prefix: every broadcast-scoped admin cache.
  broadcasts: () => [...adminKeys.all, 'broadcasts'] as const,

  broadcastsList: (params: BroadcastsPageParams) =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'list', params] as const,
      queryFn: () => fetchAdminBroadcasts(params),
    }),

  broadcastStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'status-counts'] as const,
      queryFn: fetchAdminBroadcastStatusCounts,
    }),

  broadcastAudience: (spec: AudienceSpec) =>
    queryOptions({
      queryKey: [...adminKeys.broadcasts(), 'audience', spec] as const,
      queryFn: () => fetchAdminBroadcastAudience(spec),
    }),

  // Inner-node prefix: every moderation-scoped admin cache.
  moderation: () => [...adminKeys.all, 'moderation'] as const,

  moderationQueue: (params: AdminReportQueueInput) =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'queue', params] as const,
      queryFn: () => fetchAdminModerationQueue(params),
    }),

  moderationReport: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'report', id] as const,
      queryFn: () => fetchAdminModerationReport(id),
      enabled: id.length > 0,
    }),

  moderationStats: () =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'stats'] as const,
      queryFn: fetchAdminModerationStats,
    }),

  moderationStatusCounts: () =>
    queryOptions({
      queryKey: [...adminKeys.moderation(), 'status-counts'] as const,
      queryFn: fetchAdminModerationStatusCounts,
    }),

  // Inner-node prefix: every admin-resource cache.
  resources: () => [...adminKeys.all, 'resources'] as const,

  resourcesList: (params: ListAdminResourcesInput) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'list', params] as const,
      queryFn: () => fetchAdminResourcesPage(params),
    }),

  resourceDetail: (id: string) =>
    queryOptions({
      queryKey: [...adminKeys.resources(), 'detail', id] as const,
      queryFn: () => fetchAdminResourceDetail(id),
      enabled: id.length > 0,
    }),

  engagementList: (params: EngagementListParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'engagement', 'list', params] as const,
      queryFn: () => fetchAdminEngagementList(params),
    }),

  engagementDetail: (params: EngagementDetailParams) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'engagement', 'detail', params] as const,
      queryFn: () => fetchAdminEngagementDetail(params),
    }),

  workers: () =>
    queryOptions({
      queryKey: [...adminKeys.all, 'workers'] as const,
      queryFn: fetchAdminWorkers,
    }),

  sqsDepths: () =>
    queryOptions({
      queryKey: [...adminKeys.all, 'sqs-depths'] as const,
      queryFn: fetchAdminSqsDepths,
    }),

  outboxLag: () =>
    queryOptions({
      queryKey: [...adminKeys.all, 'outbox-lag'] as const,
      queryFn: fetchAdminOutboxLag,
    }),

  partitionCoverage: () =>
    queryOptions({
      queryKey: [...adminKeys.all, 'partition-coverage'] as const,
      queryFn: fetchAdminPartitionCoverage,
    }),

  health: () =>
    queryOptions({
      queryKey: [...adminKeys.all, 'health'] as const,
      queryFn: fetchAdminHealth,
    }),

  circlesPicker: (params: PickerInput = {}) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'circles-picker', params] as const,
      queryFn: () => fetchAdminCirclesForPicker(params),
    }),

  networksPicker: (params: PickerInput = {}) =>
    queryOptions({
      queryKey: [...adminKeys.all, 'networks-picker', params] as const,
      queryFn: () => fetchAdminNetworksForPicker(params),
    }),

  // Infinite picker leaves — share the flat picker's key prefix; cursor via
  // pageParam. The flat factories above remain for single-page callers.
  circlesPickerInfinite: (params: Omit<PickerInput, 'cursor'> = {}) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.all, 'circles-picker', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminCirclesForPicker({
          ...params,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),

  networksPickerInfinite: (params: Omit<PickerInput, 'cursor'> = {}) =>
    infiniteQueryOptions({
      queryKey: [...adminKeys.all, 'networks-picker', params] as const,
      queryFn: ({ pageParam }) =>
        fetchAdminNetworksForPicker({
          ...params,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }),
};

// ---- Client fetchers ----

export async function fetchAdminAnalytics(
  windowDays: AnalyticsWindowDays = DEFAULT_ANALYTICS_WINDOW_DAYS,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/analytics/overview',
    {
      params: {
        query: { days: String(windowDays) as '30' | '90' | '180' | '365' },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminMembersPage(params: ListAdminMembersInput) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/members', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminMemberDetail(userId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/admin/members/{id}', {
    params: { path: { id: userId } },
  });
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchAdminMemberStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/members/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminInvitations(params: InvitationsPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/invitations', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminGroupInvites(params: GroupInvitesPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/group-invites', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminAudit(params: AuditPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/audit-log', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminConsent(params: ConsentPageParams) {
  const client = getApiClient();
  // `granted` travels as 'true'/'false' on the wire — the server's query
  // schema is an enum+transform because z.coerce.boolean() treated the
  // string "false" as true.
  const { granted, ...rest } = params;
  const { data, error } = await client.GET(
    '/api/v1/admin/consent-events',
    {
      params: {
        query: {
          ...rest,
          ...(granted === undefined ? {} : { granted: granted ? ('true' as const) : ('false' as const) }),
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminDsr(params: DsrPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/dsr/requests',
    {
      params: {
        query: {
          cursor: params.cursor,
          pageSize: params.pageSize,
          status: params.status,
          kind: params.kind as
            | 'access'
            | 'rectification'
            | 'erasure'
            | 'restriction'
            | 'portability'
            | 'objection'
            | undefined,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminDsrDetail(id: string) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/dsr/requests/{id}',
    { params: { path: { id } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminSensitiveRead(
  params: SensitiveReadPageParams,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/sensitive-read',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcasts(params: BroadcastsPageParams) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/broadcasts', {
    params: {
      query: {
        cursor: params.cursor,
        pageSize: params.pageSize,
        status: params.status,
      },
    },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcastStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/broadcasts/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminBroadcastAudience(spec: AudienceSpec) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/broadcasts/audience-resolve',
    { params: { query: { spec: JSON.stringify(spec) } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationQueue(
  params: AdminReportQueueInput,
) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/reports',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationReport(reportId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/moderation/reports/{id}',
    { params: { path: { id: reportId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data;
}

export async function fetchAdminModerationStats() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/stats',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminModerationStatusCounts() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/moderation/status-counts',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminResourcesPage(
  params: ListAdminResourcesInput,
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/resources', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminResourceDetail(resourceId: string) {
  const client = getApiClient();
  const { data, error, response } = await client.GET(
    '/api/v1/admin/resources/{id}',
    { params: { path: { id: resourceId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export async function fetchAdminEngagementList(params: EngagementListParams) {
  const client = getApiClient();
  if (params.scope === 'circles') {
    const { data, error } = await client.GET(
      '/api/v1/admin/circles/engagement',
      {
        params: {
          query: {
            period: params.period,
            sortBy: params.sortBy,
            sortDir: params.sortDir,
            pageSize: params.pageSize,
          },
        },
      },
    );
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/admin/networks/engagement',
    {
      params: {
        query: {
          period: params.period,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
          pageSize: params.pageSize,
        },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminEngagementDetail(
  params: EngagementDetailParams,
) {
  const client = getApiClient();
  if (params.scope === 'circle') {
    const { data, error } = await client.GET(
      '/api/v1/admin/circles/{id}/engagement',
      {
        params: {
          path: { id: params.id },
          query: { period: params.period },
        },
      },
    );
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/admin/networks/{id}/engagement',
    {
      params: {
        path: { id: params.id },
        query: { period: params.period },
      },
    },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminWorkers() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/workers', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminSqsDepths() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/sqs-depths', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminOutboxLag() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/outbox-lag', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminPartitionCoverage() {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/partition-coverage',
    {},
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminHealth() {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/health', {});
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminCirclesForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/circles', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchAdminNetworksForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/admin/networks', {
    params: { query: params },
  });
  if (data === undefined) throwApiError(error);
  return data;
}

export async function searchAdminMembersForPicker(params: PickerInput = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET(
    '/api/v1/admin/members/search',
    { params: { query: params } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

// ---- Hooks ----

export function useAdminAnalytics(
  windowDays: AnalyticsWindowDays = DEFAULT_ANALYTICS_WINDOW_DAYS,
  options?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<typeof fetchAdminAnalytics>>,
      Error,
      Awaited<ReturnType<typeof fetchAdminAnalytics>>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: adminKeys.analytics(windowDays).queryKey,
    queryFn: () => fetchAdminAnalytics(windowDays),
    ...options,
  });
}

export function useAdminMembersPage(params: ListAdminMembersInput) {
  // Flat reader reusing the infinite members-list leaf's key; supplies its own
  // non-paginated queryFn + keepPreviousData.
  return useQuery({
    queryKey: adminKeys.membersList(params).queryKey,
    queryFn: () => fetchAdminMembersPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useInfiniteAdminMembers(
  input: Omit<ListAdminMembersInput, 'cursor'>,
) {
  return useInfiniteQuery(adminKeys.membersList(input));
}

export function useAdminMemberDetail(userId: string) {
  return useQuery(adminKeys.memberDetail(userId));
}

export function useAdminMemberStatusCounts() {
  return useQuery(adminKeys.memberStatusCounts());
}

export function useAdminInvitations(params: InvitationsPageParams) {
  return useQuery(adminKeys.invitations(params));
}

export function useAdminGroupInvites(params: GroupInvitesPageParams) {
  return useQuery(adminKeys.groupInvites(params));
}

export function useAdminAudit(params: AuditPageParams) {
  return useQuery(adminKeys.audit(params));
}

export function useAdminConsent(params: ConsentPageParams) {
  return useQuery(adminKeys.consent(params));
}

export function useAdminDsr(params: DsrPageParams) {
  return useQuery(adminKeys.dsr(params));
}

export function useAdminDsrDetail(id: string) {
  return useQuery(adminKeys.dsrDetail(id));
}

export function useAdminSensitiveRead(params: SensitiveReadPageParams) {
  return useQuery(adminKeys.sensitiveRead(params));
}

export function useAdminBroadcasts(params: BroadcastsPageParams) {
  return useQuery(adminKeys.broadcastsList(params));
}

export function useAdminBroadcastStatusCounts() {
  return useQuery(adminKeys.broadcastStatusCounts());
}

export function useAdminBroadcastAudience(spec: AudienceSpec, enabled = true) {
  return useQuery({ ...adminKeys.broadcastAudience(spec), enabled });
}

export function useAdminModerationQueue(params: AdminReportQueueInput) {
  return useQuery(adminKeys.moderationQueue(params));
}

export function useAdminModerationReport(reportId: string) {
  return useQuery(adminKeys.moderationReport(reportId));
}

export function useAdminModerationStats() {
  return useQuery(adminKeys.moderationStats());
}

export function useAdminModerationStatusCounts() {
  return useQuery(adminKeys.moderationStatusCounts());
}

export function useAdminResourcesPage(params: ListAdminResourcesInput) {
  return useQuery(adminKeys.resourcesList(params));
}

export function useAdminResourceDetail(resourceId: string) {
  return useQuery(adminKeys.resourceDetail(resourceId));
}

export function useAdminEngagementList(params: EngagementListParams) {
  return useQuery(adminKeys.engagementList(params));
}

export function useAdminEngagementDetail(params: EngagementDetailParams) {
  return useQuery(adminKeys.engagementDetail(params));
}

export function useAdminWorkers() {
  return useQuery(adminKeys.workers());
}

export function useAdminHealth() {
  return useQuery(adminKeys.health());
}

export function useAdminCirclesForPicker(params: PickerInput = {}) {
  return useQuery(adminKeys.circlesPicker(params));
}

export function useAdminNetworksForPicker(params: PickerInput = {}) {
  return useQuery(adminKeys.networksPicker(params));
}

export function useInfiniteCirclesPicker(
  params: Omit<PickerInput, 'cursor'> = {},
) {
  return useInfiniteQuery(adminKeys.circlesPickerInfinite(params));
}

export function useInfiniteNetworksPicker(
  params: Omit<PickerInput, 'cursor'> = {},
) {
  return useInfiniteQuery(adminKeys.networksPickerInfinite(params));
}
